import { GlucoseUnit } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActionSubmit,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import dayjs from "dayjs"
import {
  useCallback,
  useState,
  useMemo,
} from "react"
import type { FormEventHandler } from "react"

import type { RegistrationForm } from "./RegistrationForm"
import { RegistrationPage } from "./RegistrationPage"
import styles from "./SignUp.module.scss"
import {
  AccountInformationForm,
  CountryProtectedPage,
  FeatureAdvisoryForm,
  OptionalConsentNoticesForm,
  PasswordForm,
  PersonalInformationForm,
  PublicPageHeader,
  RequiredConsentNoticesForm,
} from "src/components"
import {
  usePolyglot,
  useOverlayLoader,
  useCountrySelectionContext,
} from "src/contexts"
import { useCreateAccount } from "src/hooks"
import { useInitialConsents } from "src/hooks/consent/useInitialConsents"
import { SignUpErrorToast } from "src/toasts"
import {
  injectProps,
  getSugarmateError,
  getDateFromLocaleDateString,
} from "src/utils"

const polyglotPrefix = "pages.sign_up."

const registrationPages = Object.freeze([
  RegistrationPage.FeatureAdvisory,
  RegistrationPage.RequiredConsentNotices,
  RegistrationPage.OptionalConsentNotices,
  RegistrationPage.PersonalInformation,
  RegistrationPage.AccountInformation,
  RegistrationPage.Password,
])

const registrationPagesObject = Object.freeze({
  [RegistrationPage.FeatureAdvisory]: FeatureAdvisoryForm,
  [RegistrationPage.PersonalInformation]: PersonalInformationForm,
  [RegistrationPage.RequiredConsentNotices]: RequiredConsentNoticesForm,
  [RegistrationPage.OptionalConsentNotices]: OptionalConsentNoticesForm,
  [RegistrationPage.AccountInformation]: AccountInformationForm,
  [RegistrationPage.Password]: PasswordForm,
})

const defaultRegistrationForm: RegistrationForm = Object.freeze({
  token: "",
  firstname: "",
  lastname: "",
  dateOfBirth: "",
  emailAddress: "",
  phoneNumber: "",
  password: "",
  passwordConfirmation: "",
  nickname: "",
  consents: {},
})

interface Props {
  onAccountCreate?: (form: RegistrationForm) => Promise<void>;
  goBack: () => void;
}

export function RegistrationFlow(props: Props) {
  const {
    goBack,
    onAccountCreate,
  } = props

  const { country } = useCountrySelectionContext()

  const [
    registrationForm,
    setRegistrationForm,
  ] = useState<RegistrationForm>(
    defaultRegistrationForm,
  )

  const updateRegistrationForm = useCallback(
    (updatedRegistrationForm: Partial<RegistrationForm>) => {
      setRegistrationForm({
        ...registrationForm,
        ...updatedRegistrationForm,
      })
    },
    [
      registrationForm,
      setRegistrationForm,
    ],
  )

  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()
  const createAccount = useCreateAccount()

  const { data: consents } = useInitialConsents(
    country?.country_code ?? "",
    polyglot.locale(),
  )

  const pages = useMemo(
    () => registrationPages.filter((page) => {
      if (page === RegistrationPage.RequiredConsentNotices) {
        return (
          consents?.required?.consents &&
            consents?.required?.consents.length > 0
        )
      }

      if (page === RegistrationPage.OptionalConsentNotices) {
        return (
          consents?.optional?.consents &&
            consents?.optional?.consents.length > 0
        )
      }

      return true
    }),
    [consents],
  )

  const [
    registrationPage,
    setRegistrationPage,
  ] = useState<RegistrationPage>(
    pages[0],
  )

  const previousPage: RegistrationPage | undefined = useMemo(
    () => {
      const index = pages.indexOf(registrationPage)
      return pages[index - 1]
    },
    [
      pages,
      registrationPage,
    ],
  )

  const nextPage: RegistrationPage | undefined = useMemo(
    () => {
      const index = pages.indexOf(registrationPage)
      return pages[index + 1]
    },
    [
      pages,
      registrationPage,
    ],
  )

  const onBackClick = useCallback(
    () => {
      if (previousPage) {
        setRegistrationPage(previousPage)
      } else {
        goBack()
      }
    },
    [
      previousPage,
      goBack,
    ],
  )

  const createAccountCallback = useCallback(
    async () => {
      setIsOverlayLoaderVisible(true)

      try {
        if (!onAccountCreate) {
          throw new Error("missing account creation handler")
        }

        if (!consents) {
          throw new Error("Consents data is not available")
        }

        if (!createAccount) {
          throw new Error("Unable to create account")
        }

        const dateOfBirth = dayjs(
          getDateFromLocaleDateString(
            polyglot.locale(),
            registrationForm.dateOfBirth,
          ),
        ).format("YYYY/MM/DD")

        await createAccount({
          token: registrationForm.token,
          email: registrationForm.emailAddress,
          password: registrationForm.password,
          country: country?.country_code ?? "",
          units: country?.units ?? GlucoseUnit.MilligramsPerDeciliter,
          firstname: registrationForm.firstname,
          lastname: registrationForm.lastname,
          dateOfBirth,
          nickname: registrationForm.nickname || null,
          consents: {
            metadataId: consents.metadataId,
            metadataVersion: consents.metadataVersion,
            consents: [
              ...(consents?.required?.consents || []).map((consent) => ({
                policyId: consent.policyId,
                version: consent.version,
                accepted: registrationForm.consents[consent.policyId],
              })),
              ...(consents?.optional?.consents || []).map((consent) => ({
                policyId: consent.policyId,
                version: consent.version,
                accepted: registrationForm.consents[consent.policyId],
              })),
            ],
          },
        })

        await onAccountCreate(registrationForm)
      } catch (e) {
        const sugarmateError = getSugarmateError(e)

        const ErrorToast = injectProps(
          SignUpErrorToast,
          {
            code: sugarmateError?.code,
            status: sugarmateError?.status,
          },
        )

        showToast(
          "sign-up-error-toast",
          ErrorToast,
        )
      }

      setIsOverlayLoaderVisible(false)
    },
    [
      onAccountCreate,
      consents,
      createAccount,
      registrationForm,
      country?.country_code,
      country?.units,
      showToast,
      polyglot,
      setIsOverlayLoaderVisible,
    ],
  )

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      if (nextPage) {
        setRegistrationPage(nextPage)
      } else {
        void createAccountCallback()
      }
    },
    [
      nextPage,
      setRegistrationPage,
      createAccountCallback,
    ],
  )

  const Page = registrationPagesObject[registrationPage]

  return (
    <CountryProtectedPage>
      <div className={styles.container}>
        <PublicPageHeader onBackClick={onBackClick}>
          {polyglot.t(`${polyglotPrefix}title`)}
        </PublicPageHeader>
        <Page
          form={registrationForm}
          updateForm={updateRegistrationForm}
          onSubmit={onSubmit}
          consents={
            registrationPage === RegistrationPage.RequiredConsentNotices
              ? consents?.required
              : consents?.optional
          }
        >
          <ActionSubmit
            disabled={!onAccountCreate}
            id="submit-button"
            className={styles.submit}
            value={polyglot.t(
              `${polyglotPrefix}${nextPage ? "next_cta" : "submit_cta"}`,
            )}
          />
        </Page>
      </div>
    </CountryProtectedPage>
  )
}
